import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import BrandLogo from "./BrandLogo";
import { 
  LayoutDashboard, 
  Users, 
  GitPullRequest, 
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Building2,
  ShieldCheck,
  TicketPercent,
  Wallet,
  ScrollText,
  Activity
} from "lucide-react";

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const permissionByPath = {
    "/admin": "dashboard",
    "/admin/companies": "companies",
    "/admin/consultants": "consultants",
    "/admin/referrals": "referrals",
    "/admin/users": "users"
  };

  const baseMenuItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/companies", icon: Building2, label: "Empresas" },
    { path: "/admin/consultants", icon: Users, label: "Consultores" },
    { path: "/admin/accountants", icon: Users, label: "Contadores" },
    { path: "/admin/referrals", icon: GitPullRequest, label: "Encaminhamentos" },
    { path: "/admin/coupons", icon: TicketPercent, label: "Cupons" },
    { path: "/admin/users", icon: ShieldCheck, label: "Usuários e Acessos" },
  ];
  const programmerItems = [
    { path: "/programmer/analytics", icon: Activity, label: "Analytics" },
    { path: "/programmer/finance", icon: Wallet, label: "Financeiro" },
    { path: "/programmer/audit", icon: ScrollText, label: "Auditoria" },
  ];
  const menuItems = user?.role === "programmer" ? [...baseMenuItems, ...programmerItems] : baseMenuItems;
  const isProgrammer = user?.role === "programmer";

  const allowedMenuItems = menuItems.filter((item) => {
    if (user?.role === "admin" || user?.role === "programmer") return true;
    if (user?.role !== "employee") return item.path === "/admin/users";
    const permission = permissionByPath[item.path];
    if (!permission) return false;
    return (user?.permissions || []).includes(permission);
  });

  const renderMenuItems = (isMobile = false) =>
    allowedMenuItems.map((item) => {
      const isActive = location.pathname === item.path;
      return (
        <Link
          key={`${isMobile ? "mobile" : "desktop"}-${item.path}`}
          to={item.path}
          onClick={() => isMobile && setMobileOpen(false)}
          className={`
            flex items-center gap-3 px-4 py-3 transition-colors
            ${isActive ? "bg-white/10 border-r-4 border-gold" : "hover:bg-white/5"}
            ${!isMobile && collapsed ? "justify-center" : ""}
          `}
          title={!isMobile && collapsed ? item.label : ""}
          data-testid={`admin-menu-${item.path.split('/').pop() || 'dashboard'}`}
        >
          <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? "text-gold" : "text-white/70"}`} />
          {(isMobile || !collapsed) && (
            <span className={`text-sm ${isActive ? "font-medium" : "text-white/80"}`}>
              {item.label}
            </span>
          )}
        </Link>
      );
    });

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside 
        className={`
          hidden md:flex bg-navy text-white transition-all duration-300 flex-col
          ${collapsed ? "w-16" : "w-64"}
        `}
      >
        {/* Logo */}
        <div className={`p-4 border-b border-white/10 flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <BrandLogo
            compact
            size="sm"
            className="flex-shrink-0"
          />
          {!collapsed && (
            <div>
              <span className="block font-serif text-base font-semibold tracking-wide text-white">CREDITA CGE</span>
              <span className="block text-xs text-white/60">{isProgrammer ? "Programador" : "Administração"}</span>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="flex-1 py-4">
          {renderMenuItems(false)}
        </nav>

        {/* User & Collapse */}
        <div className="p-4 border-t border-white/10">
          {!collapsed && (
            <div className="mb-4">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{user?.email}</p>
            </div>
          )}
          <div className={`flex ${collapsed ? "flex-col gap-2" : "justify-between"}`}>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10"
              title="Sair"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              {!collapsed && <span className="ml-2">Sair</span>}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCollapsed(!collapsed)}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="toggle-sidebar-btn"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <div className={`md:hidden fixed inset-0 z-40 transition-opacity ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <button
          className="absolute inset-0 bg-black/40"
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        />
      </div>
      <aside
        className={`
          md:hidden fixed top-0 left-0 z-50 h-full w-72 bg-navy text-white transition-transform duration-300 flex flex-col
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo compact size="sm" className="flex-shrink-0" />
            <div>
              <span className="block font-serif text-base font-semibold tracking-wide text-white">CREDITA CGE</span>
              <span className="block text-xs text-white/60">{isProgrammer ? "Programador" : "Administração"}</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileOpen(false)}
            className="text-white/70 hover:text-white hover:bg-white/10"
            aria-label="Fechar menu"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <nav className="flex-1 py-4">
          {renderMenuItems(true)}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="mb-4">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-white/60 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => {
              setMobileOpen(false);
              await handleLogout();
            }}
            className="text-white/70 hover:text-white hover:bg-white/10"
            title="Sair"
            data-testid="admin-logout-btn-mobile"
          >
            <LogOut className="h-4 w-4" />
            <span className="ml-2">Sair</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="md:hidden sticky top-0 z-30 bg-white border-b px-4 py-3 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMobileOpen(true)}
            data-testid="open-mobile-sidebar-btn"
          >
            <Menu className="h-4 w-4 mr-2" />
            Menu
          </Button>
          <BrandLogo size="sm" className="max-w-[140px]" />
        </div>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
